import { t } from 'i18next';
import { isNil } from 'lodash-es';
import * as yup from 'yup';

import { CompanySettings } from '~hooks/useCompanySettings/models';

type DepartmentValue = { name?: string | null } | null | undefined;

export function getDepartmentRateRequirements(departmentName: string | null | undefined) {
  const name = departmentName ?? '';

  return {
    requiresMaterialRate: /\bfob\b/i.test(name),
    requiresFreightRate: /\bdeliver(y|ed)\b/i.test(name),
  };
}

export const isBlankRateValue = (value: unknown) =>
  isNil(value) || String(value).trim() === '';

export const requiredMessage = (field: string) =>
  t('form_validation_errors.required', { field });

// Null when no setting can require a material rate, so the schema stays untouched.
export function makeMaterialRateRequirement(
  recordCompanySettings: CompanySettings | undefined,
  canManageMaterialRates: boolean,
) {
  const isAlwaysRequired = recordCompanySettings?.isEnabled('require_material_rate');
  const isRequiredByDepartment = recordCompanySettings?.isEnabled(
    'require_rates_by_department_name',
  );

  const isAnyMaterialRateRuleOn = isAlwaysRequired || isRequiredByDepartment;
  if (!canManageMaterialRates || !isAnyMaterialRateRuleOn) {
    return null;
  }

  return (department: DepartmentValue) =>
    Boolean(isAlwaysRequired) ||
    getDepartmentRateRequirements(department?.name).requiresMaterialRate;
}

export const isFreightRateRequiredForDepartment = (
  recordCompanySettings: CompanySettings,
  department: DepartmentValue,
) =>
  recordCompanySettings.isEnabled('require_rates_by_department_name') &&
  getDepartmentRateRequirements(department?.name).requiresFreightRate;

export const makeRatePriceSchema = () =>
  yup
    .number()
    .transform((_, value) => (isBlankRateValue(value) ? undefined : Number(value)))
    .min(0)
    .typeError(t('form_validation_errors.numbers_only'))
    .notRequired();
